import AxiosService from '@/Services/AxiosService';
import LoaderService from '@/Services/LoaderService';
import { Inject, Service } from 'vue-di-container';
import { RequestModel } from './RequestModel';
import { AttachmentCategoryType } from '@/shared/Enums/AttachmentCategory';

@Service()
export default class AddRequestService {
    @Inject(AxiosService) public axiosService!: AxiosService;
    @Inject(LoaderService) public loaderService!: LoaderService;

    public async post(model: RequestModel) {
        this.loaderService.ShowLoader();
        try {
            const url = window.$config.HostName + 'api/sender-requests';
            const result = await this.axiosService.axiosInstance.post(url, model);
            this.loaderService.HideLoader();
            return result.data;
        } catch (error) {
            this.loaderService.HideLoader();
            return false;
        }
    }
    public async getAttachmentCategory(
        name: string,
        pageIndex: string,
        pageSize: string): Promise<any> {
        this.loaderService.ShowLoader();
        try {
            const url = window.$config.HostName + 'api/attachmentCategory?name=""'
                + '&pageIndex=' + pageIndex
                + '&pageSize=' + pageSize;
            const result = await this.axiosService.axiosInstance.get(url);
            this.loaderService.HideLoader();
            return result.data;
        } catch (exception) {
            this.loaderService.HideLoader();
            return false;
        }
    }
    public async addRequestEmail(ids: string[]): Promise<any> {
        try {
            const url = window.$config.HostName + 'api/sender-requests/send-add-request-email';
            const result = await this.axiosService.axiosInstance.put(url, ids);
            return result.data;
        } catch (exception) {
            return false;
        }
    }

    public async getSuggestedNames(clientName: string, senderType: number, customerType: number) {
            this.loaderService.ShowLoader();
            try {
                const url = window.$config.HostName + 'api/senders/getSuggestedNames?clientName=' + clientName + '&senderType=' + senderType + '&customerType=' + customerType;
                const result = await this.axiosService.axiosInstance.get(url);
                this.loaderService.HideLoader();
                return result.data;
            } catch (error) {
                this.loaderService.HideLoader();
                return false;
            }
        }
    public async verify(unifiedNumber: string) {
        this.loaderService.ShowLoader();
        try {
            const url = window.$config.HostName + 'api/mci-controller/verifyCR?unifiedNumber=' + unifiedNumber ;
            const result = await this.axiosService.axiosInstance.get(url);
            this.loaderService.HideLoader();
            return result.data;
        } catch (error) {
            this.loaderService.HideLoader();
            return false;
        }
    }

    public async validateAttachments(model: RequestModel) {
        // this.loaderService.ShowLoader();
        const url = `${window.$config.HostName}api/sender-request-attachment-ai`;
        const form = new FormData();

        form.append('entity_type', this.mapEntityType(model.customerType));
        form.append('auth_number', model.crNumber || '123456789');
        form.append('sender_type', 'White List');
        form.append('enterprise_unified_number', model.enterpriseUnifiedNumber || '');
        form.append(
            'sender_id',
            Array.isArray(model.senderNames) && model.senderNames.length
                ? model.senderNames.map(s => s.senderName).join(', ')
                : ''
        );
        form.append('entity_name', model.clientName || '');
        form.append('authorized_name_to_use', model.representativeName || '');
        form.append('authorized_id', model.representativeIdNumber || '0');
        form.append('authorized_email_to_use', model.representativeEmail || '');
        form.append('authorized_mobile_to_use',('0' + model.representativeMobile));
        form.append('expiry_date', this.formatExpiryDate(model.expiryDate));

        form.append('start_protection', this.formatHijriFromIso(model.protectionDateFrom || ''));
        form.append('end_protection', this.formatHijriFromIso(model.protectionDateTo || ''));

        form.append('brand_name', model.trademarkName || '');

        const typeToKey: { [key: string]: string } = {};
        typeToKey[String(AttachmentCategoryType.DelegationLetter).toLowerCase()] = 'authorization_letter';
        typeToKey[String(AttachmentCategoryType.CommercialRegistrationDocument).toLowerCase()] = 'commercial_register_doc';
        typeToKey[String(AttachmentCategoryType.HRSDLicense).toLowerCase()] = 'hrsd_license_doc';
        typeToKey[String(AttachmentCategoryType.SaudiDomain).toLowerCase()] = 'domain_certificate';
        typeToKey[String(AttachmentCategoryType.Trademark).toLowerCase()] = 'intellectual_property_certificate';

        const atts = model.senderRequestAttachments || [];
        for (let i = 0; i < atts.length; i++) {
            const att = atts[i];
            if (!att || !att.file) continue;

            const key = typeToKey[String(att.attachmentType || '').toLowerCase()];
            if (key) {
                form.append(key, att.file, att.fileName || 'file');
            }
        }

        try {
            const resp = await this.axiosService.axiosInstance.post(url, form);
            // this.loaderService.HideLoader();
            return { isSuccess: true, data: resp.data, errorMessage: '' };
        } catch (err) {
            const e = err as any;
            const payload = e && e.response && e.response.data;
            const msg =
                (payload && payload.exception && payload.exception.Message) ||
                (payload && payload.message) ||
                e.message;
            return { isSuccess: false, data: null, errorMessage: msg };
        }
    }

    private formatHijriFromIso(isoDate: string): string {
        if (!isoDate) return '';
        const parts = isoDate.split('-'); 
        if (parts.length !== 3) return '';
        const y = parseInt(parts[0], 10);
        const m = parseInt(parts[1], 10);
        const d = parseInt(parts[2], 10);
        if (!y || !m || !d) return '';

        const greg = new Date(Date.UTC(y, m - 1, d));

        try {
            const fmt = new Intl.DateTimeFormat('en-u-ca-islamic-umalqura', {
                timeZone: 'UTC',
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
            });
            const hijriParts = fmt.formatToParts(greg);

            let year = '';
            let month = '';
            let day = '';
            for (let i = 0; i < hijriParts.length; i++) {
                const p = hijriParts[i];
                if (p.type === 'year') year = p.value;
                else if (p.type === 'month') month = p.value;
                else if (p.type === 'day') day = p.value;
            }

            if (!year || !month || !day) return '';
            return year + '/' + month + '/' + day;
        } catch (e) {
            return '';
        }
    }


    private mapEntityType(type: number): string {
        if (type === 1) return 'Government';
        if (type === 2) return 'Charity';
        if (type === 3) return 'Private';
        return 'Private';
    }

    private formatExpiryDate(isoDate: string): string {
        if (!isoDate) return '';
        const parts = isoDate.split('-'); // yyyy-mm-dd
        if (parts.length !== 3) return '';
        const [year, month, day] = parts;
        const d = parseInt(day, 10);
        const m = parseInt(month, 10);
        if (isNaN(d) || isNaN(m) || !year) return '';
        return `${year}/${String(m).padStart(2, '0')}/${String(d).padStart(2, '0')}`;
    }

    public async checkSenderNameAvailability(senderName: string) {
        this.loaderService.ShowLoader();
        try {
            const url = window.$config.HostName + 'api/sender-requests/check-sender-name-availability?senderName=' + senderName ;
            const result = await this.axiosService.axiosInstance.get(url);
            this.loaderService.HideLoader();
            return result.data;
        } catch (error) {
            this.loaderService.HideLoader();
            return false;
        }
    }

}
